import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../common/AppProvider';
import { translations } from '../translations/translations';
import { AutomationTask, TaskLog } from '../tasks/types';
import { stringReplace } from '../common/utils';
import { AutomateError, getTaskLogs } from '../api';
import ItemList from '../common/ItemList';
import Select from 'react-select';

import './task-logs.css';

type EventTypeFilterOption = { label: string | undefined; value: string | undefined };

const TaskLogs = () => {
    const { language, tasks, setIsLoading, alertError } = useApp();
    const [searchParams] = useSearchParams();

    const [task, setTask] = useState<AutomationTask>();
    const [taskLogs, setTaskLogs] = useState<TaskLog[]>([]);
    const [filteredTaskLogs, setFilteredTaskLogs] = useState<TaskLog[]>([]);
    const [selectedEventType, setSelectedEventType] = useState<EventTypeFilterOption | null>(null);

    const sortByDate = (taskLogs: TaskLog[]) => {
        return taskLogs.sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);

            return dateB.valueOf() - dateA.valueOf();
        });
    };

    useEffect(() => {
        if (selectedEventType === null) {
            setFilteredTaskLogs(taskLogs);
            return;
        }

        setFilteredTaskLogs(taskLogs.filter((log) => log.eventType === selectedEventType?.value));
    }, [selectedEventType, taskLogs]);

    useEffect(() => {
        if (task?.id) {
            setIsLoading(true);

            getTaskLogs(task.id)
                .then((response: TaskLog[]) => {
                    setTaskLogs(sortByDate(response));
                })
                .catch((e: AutomateError) =>
                    alertError({
                        message: translations[language].ERROR_getting_task_logs,
                        traceId: e.json?.traceId,
                    }),
                )
                .finally(() => setIsLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [task, setIsLoading, language]);

    useEffect(() => {
        const taskId = searchParams.get('taskId');
        setTask(tasks.find((t) => t.id === taskId));
    }, [searchParams, tasks]);

    return (
        <ItemList
            backTo={`/tasks/${task?.id}`}
            title={stringReplace(translations[language].TASK_LOGS_header, task?.displayName ?? '')}
            toolbarFilters={
                <Select
                    className="react-select-container"
                    classNamePrefix="react-select"
                    options={[
                        {
                            label: translations[language].TASK_LOGS_created_label,
                            value: 'taskCreated',
                        },
                        {
                            label: translations[language].TASK_LOGS_enabled_label,
                            value: 'enabled',
                        },
                        {
                            label: translations[language].TASK_LOGS_disabled_label,
                            value: 'disabled',
                        },
                        {
                            label: translations[language].TASK_LOGS_trigger_activated_label,
                            value: 'triggerActivated',
                        },
                        {
                            label: translations[language].TASK_LOGS_action_fired_label,
                            value: 'actionFired',
                        },
                        {
                            label: translations[language].TASK_LOGS_action_failed_label,
                            value: 'actionFailed',
                        },
                    ].filter((option) => option.value !== selectedEventType?.value)}
                    onChange={setSelectedEventType}
                    placeholder={translations[language].TASK_LOGS_filter_placeholder}
                    value={selectedEventType}
                    menuPortalTarget={document.querySelector<HTMLElement>('.automate-root')}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    isMulti={false}
                    isClearable={true}
                />
            }
            items={filteredTaskLogs}
            itemContent={(taskLog: TaskLog) => (
                <li className="task-log-layout ta-tile" key={taskLog.id}>
                    <span className="text md">{taskLog.eventName}</span>
                    <span className="text md" data-testid="task-log-date">
                        {new Date(taskLog.createdAt).toLocaleString(navigator.language)}
                    </span>
                </li>
            )}
        />
    );
};

export default TaskLogs;
